import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, ExternalLink, Lock } from "lucide-react";
import styles from "@/components/library/BookDetailVisual.module.css";
import { Button } from "@/components/ax/Button";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { BookCover } from "@/components/library/BookCover";
import { BriefSection } from "@/components/library/BriefSection";
import { InsightList } from "@/components/library/InsightList";
import { logLibraryEvent } from "@/lib/libraryAnalytics";
import { useI18n } from "@/i18n";
import { getBook } from "@/lib/libraryStore";

const BOOK_ATTRIBUTION_COPY = {
  id: {
    openBadge: "Edisi Sumber Terbuka",
    authorsLabel: "Penulis sumber:",
    summarizedNote: "Disajikan dan diringkas oleh AXORIS sesuai lisensi sumber.",
    readAtSource: "Baca di sumber",
    sourceLicense: "Lisensi sumber",
  },
  en: {
    openBadge: "Open Source Edition",
    authorsLabel: "Source authors:",
    summarizedNote: "Presented and summarized by AXORIS under the source license.",
    readAtSource: "Read at source",
    sourceLicense: "Source license",
  },
};

function resolveBookAttribution(book) {
  if (!book || typeof book !== "object") {
    return { showAttribution: false };
  }
  const origin = book.origin === "open" ? "open" : "original";
  const rightsClass = book.rightsClass || (origin === "original" ? "HOST" : "PENDING_REVIEW");
  const rawAttr = book.attribution;

  if (origin !== "open" && !rawAttr) {
    return { showAttribution: false };
  }

  if (typeof rawAttr === "string") {
    const trimmed = rawAttr.trim();
    return {
      showAttribution: origin === "open" || trimmed.length > 0,
      rightsClass,
      text: trimmed || null,
      authors: [],
      licenseCode: null,
      licenseUrl: null,
      sourceUrl: null,
      summarizedByAxoris: rightsClass === "HOST_ATTRIBUTION",
    };
  }

  const attrObj =
    rawAttr && typeof rawAttr === "object" && !Array.isArray(rawAttr)
      ? rawAttr
      : {};

  const authors = Array.isArray(attrObj.authors)
    ? attrObj.authors.filter((a) => typeof a === "string" && a.trim().length > 0)
    : typeof attrObj.author === "string" && attrObj.author.trim().length > 0
      ? [attrObj.author.trim()]
      : [];

  return {
    showAttribution: true,
    rightsClass,
    text: typeof attrObj.text === "string" ? attrObj.text.trim() : null,
    authors,
    licenseCode:
      typeof attrObj.licenseCode === "string" ? attrObj.licenseCode.trim() : null,
    licenseUrl:
      typeof attrObj.licenseUrl === "string" &&
      attrObj.licenseUrl.trim().startsWith("https://")
        ? attrObj.licenseUrl.trim()
        : null,
    sourceUrl:
      typeof attrObj.sourceUrl === "string" &&
      attrObj.sourceUrl.trim().startsWith("https://")
        ? attrObj.sourceUrl.trim()
        : null,
    summarizedByAxoris:
      attrObj.summarizedByAxoris === true || rightsClass === "HOST_ATTRIBUTION",
  };
}

export default function LibraryBook() {
  const { t, lang } = useI18n();
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((n) => n + 1);

  const load = async () => {
    setState("loading");
    const res = await getBook(bookId);
    if (res.error) return setState(res.error);
    setData(res);
    setState("ready");
  };
  useEffect(() => {
    load();
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "loading") {
    return (
      <div aria-busy="true" data-testid="book-skeleton" className={styles.skeleton}>
        <div className={styles.skeletonCover} />
        <div className={styles.skeletonText}><div /><div /><div /></div>
      </div>
    );
  }
  if (state !== "ready") {
    return (
      <div role="alert" data-testid="book-error" className={styles.error}>
        <p className="m-0 text-base text-ax-text-2">{t(state === "missing" ? "book.notFound" : "book.failLoad")}</p>
        {state === "read" && <Button onClick={load} data-testid="book-retry-button" className="mt-4">{t("retry")}</Button>}
      </div>
    );
  }

  const { book, locked, progress } = data;
  const target = progress?.chapterId ?? book.chapters[0]?.id;
  const attr = resolveBookAttribution(book);
  const attrCopy = BOOK_ATTRIBUTION_COPY[lang === "en" ? "en" : "id"];

  return (
    <div data-testid="book-page" className={styles.page}>
      <Link to="/library" data-testid="book-back-link" className={styles.back}>
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        {t("book.back")}
      </Link>
      <p className={styles.eyebrow}>{t("lib.archive")}</p>
      <div className={styles.hero} data-testid="book-metadata-panel">
        <div className={styles.cover} aria-hidden="true">
          <BookCover book={book} locked={locked} />
        </div>
        <div className={styles.info}>
          <p className={styles.category} data-testid="book-category">{book.category?.[lang]}</p>
          <h1 className={styles.title} data-testid="book-title">{book.title?.[lang] ?? book.id}</h1>
          <p className={styles.summary} data-testid="book-summary">{book.summary?.[lang]}</p>
          <div className={styles.metadata}>
            <span data-testid="book-minutes">{t("lib.estimatedMinutes", { n: book.minutes })}</span>
            <span>{t("book.chapterCount", { n: book.chapters.length })}</span>
          </div>
        </div>
      </div>

      {attr.showAttribution && (
        <section
          data-testid="book-attribution-block"
          aria-label={attrCopy.openBadge}
          className={styles.attribution}
        >
          <div className={styles.attributionBadges}>
            <span className={styles.attributionBadge}>{attrCopy.openBadge}</span>
            {attr.licenseCode && (
              <span
                data-testid="book-attribution-license"
                className={styles.attributionBadge}
              >
                {attr.licenseCode}
              </span>
            )}
          </div>
          {attr.authors.length > 0 && (
            <p className="m-0" data-testid="book-attribution-authors">
              {attrCopy.authorsLabel} {attr.authors.join(", ")}
            </p>
          )}
          {attr.text && (
            <p className="m-0" data-testid="book-attribution-text">
              {attr.text}
            </p>
          )}
          {attr.summarizedByAxoris && (
            <p className="m-0" data-testid="book-attribution-note">
              {attrCopy.summarizedNote}
            </p>
          )}
          {(attr.sourceUrl || attr.licenseUrl) && (
            <div className={styles.attributionLinks}>
              {attr.sourceUrl && (
                <a
                  href={attr.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-attribution-source-link"
                  className={styles.attributionLink}
                  onClick={() => {
                    void logLibraryEvent("external_source_open", {
                      availabilityMode: "external_link",
                    });
                  }}
                >
                  {attrCopy.readAtSource}
                  <ExternalLink size={13} strokeWidth={1.5} aria-hidden="true" />
                </a>
              )}
              {attr.licenseUrl && (
                <a
                  href={attr.licenseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-attribution-license-link"
                  className={styles.attributionLink}
                  onClick={() => {
                    void logLibraryEvent("external_source_open", {
                      availabilityMode: "external_link",
                    });
                  }}
                >
                  {attrCopy.sourceLicense}
                  <ExternalLink size={13} strokeWidth={1.5} aria-hidden="true" />
                </a>
              )}
            </div>
          )}
        </section>
      )}

      {locked && (
        <p data-testid="book-locked" className={styles.locked}>
          <Lock size={16} strokeWidth={1.5} aria-hidden="true" className="mt-0.5 shrink-0" />
          {t("lib.locked")}
        </p>
      )}

      <section className={styles.chapters} aria-labelledby="book-chapters">
        <div className={styles.chapterHeader}>
          <h2 id="book-chapters" className={styles.chapterTitle}>{t("book.chapters")}</h2>
          <span className={styles.chapterCount} data-testid="book-chapter-count">{t("book.chapterCount", { n: book.chapters.length })}</span>
        </div>
        {book.chapters.length === 0 && <p className={styles.empty} data-testid="book-empty">{t("book.empty")}</p>}
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {book.chapters.map((c, i) => {
            const inner = (
              <>
                <span className="font-mono text-[13px] leading-[18px] text-ax-muted">{String(i + 1).padStart(2, "0")}</span>
                <span className="min-w-0 flex-1 text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{c.title[lang]}</span>
                <span data-testid={`chapter-minutes-${c.id}`} className="font-mono text-[13px] leading-[18px] text-ax-muted">{t("lib.minutes", { n: c.minutes })}</span>
                {locked ? <Lock size={14} strokeWidth={1.5} aria-hidden="true" /> : <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />}
              </>
            );
            const cls = "flex min-h-[56px] items-center gap-3 rounded-[12px] border border-ax-border bg-ax-surface px-4 py-2 shadow";
            return (
              <li key={c.id}>
                {locked ? (
                  <div data-testid={`chapter-row-${c.id}`} aria-disabled="true" className={`${cls} opacity-60`}>{inner}</div>
                ) : (
                  <Link to={`/library/${book.id}/bab/${c.id}`} data-testid={`chapter-row-${c.id}`} className={`${cls} transition-colors duration-150 hover:bg-ax-bg`}>
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      <PrimaryBar>
        <Button
          variant="primary"
          disabled={!locked && book.chapters.length === 0}
          onClick={() => navigate(locked ? "/akun" : `/library/${book.id}/bab/${target}`)}
          data-testid="book-primary-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          {locked ? <Lock size={18} strokeWidth={1.5} aria-hidden="true" /> : <BookOpen size={18} strokeWidth={1.5} aria-hidden="true" />}
          {locked ? t("book.restore") : t(progress?.chapterId ? "book.read" : "book.start")}
        </Button>
      </PrimaryBar>

      {!locked && book.origin === "open" && (
        <>
          <BriefSection book={book} onChanged={bump} />
          <InsightList bookId={book.id} refreshKey={refreshKey} onChanged={bump} />
        </>
      )}
    </div>
  );
}
